import { Menu, Modal } from '@mantine/core';
import PrimaryDropDown from 'app/views/components/DropDown/DropDown';
import { useDispatch } from 'react-redux';
import { LoanMergeParams } from 'app/models/loan-merge-params';
import {
    approveLoanMerge,
    deleteLoanMerge,
    rejectLoanMerge,
    submitLoanMerge,
} from './merge-daml-actions';
import { useState } from 'react';
import { useApprovedLoan } from '../loans/utilities/use-approved-loan';
import MergeLoansModal from 'app/views/pages/deals-management/merges/merge-loans-modal';
import IonIcon from '@reacticons/ionicons';
import { LoanParams } from 'app/models/loan-params';
import DeleteConfirmation from 'app/views/components/delete-confirmation/DeleteConfirmation';
import { CustomLoader } from 'app/views/components/loader/loader';

type Props = {
    row: LoanMergeParams;
};

export default function LoanMergeActions({ row }: Props) {
    const resolveLoan = useApprovedLoan();
    const [loans, setLoans] = useState<LoanParams[]>([]);
    const [openNewMerge, setOpenNewMerge] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false);
    const dispatch = useDispatch();

    // Edit needs every source loan of this merge: from the approved-loans slice
    // when the page has it, otherwise fetched by id (the Servicing page loads no
    // loans up front).
    async function openMerge() {
        const sourceLoanIds = (row.sourceLoans?.map(sl => sl.id).filter(id => id != null) || []) as string[];
        setLoans([]);
        setOpenNewMerge(true);
        const resolved = await Promise.all(sourceLoanIds.map(id => resolveLoan(id)));
        const sourceLoans = resolved.filter((l): l is LoanParams => l !== undefined);
        // every source loan or none: a merge edited against fewer loans than it
        // names would save a different merge
        if (sourceLoans.length === 0 || sourceLoans.length !== sourceLoanIds.length) {
            setOpenNewMerge(false);
            return;
        }
        setLoans(sourceLoans);
    }

    const handleDelete = () => {
        deleteLoanMerge(row, dispatch);
        setShowDeleteModal(false);
    };

    return (
        <div>
            <Modal
                size="50%"
                className="modal-body modal-body-template fit-content-modal"
                opened={openNewMerge}
                onClose={() => setOpenNewMerge(false)}
            >
                {loans.length > 0 ? (
                    <MergeLoansModal
                        open={openNewMerge}
                        setOpen={setOpenNewMerge}
                        selectedLoans={loans}
                        existingMergeData={row}
                    />
                ) : (
                    <CustomLoader />
                )}
            </Modal>
            <Menu shadow="md" width={200} position="bottom" withinPortal>
                <Menu.Target>
                    <div style={{ width: 'fit-content' }}>
                        <PrimaryDropDown>Actions</PrimaryDropDown>
                    </div>
                </Menu.Target>

                <Menu.Dropdown>
                    {row?.status?.toLowerCase() === 'draft' ? (
                        <Menu.Item
                            icon={<IonIcon className="action-ion-icon" name="swap-horizontal-outline" />}
                            onClick={() => openMerge()}
                        >
                            Edit
                        </Menu.Item>
                    ) : null}

                    {row?.status?.toLowerCase() === 'draft' ? (
                        <Menu.Item
                            icon={<IonIcon className="action-ion-icon" name="swap-horizontal-outline" />}
                            onClick={() => {
                                submitLoanMerge(row, dispatch);
                            }}
                        >
                            Submit For Approval
                        </Menu.Item>
                    ) : null}

                    {row?.status?.toLowerCase() === 'submitted' ? (
                        <Menu.Item
                            icon={<IonIcon className="action-ion-icon" name="swap-horizontal-outline" />}
                            onClick={() => {
                                approveLoanMerge(row, dispatch);
                            }}
                        >
                            Approve
                        </Menu.Item>
                    ) : null}

                    {row?.status?.toLowerCase() !== 'approved' ? (
                        <>
                            <Menu.Divider />
                            <Menu.Label>Danger zone</Menu.Label>
                        </>
                    ) : null}

                    {row?.status?.toLowerCase() === 'draft' ? (
                        <Menu.Item
                            color="red"
                            icon={<IonIcon className="action-ion-icon" name="trash-outline" />}
                            onClick={() => setShowDeleteModal(true)}
                        >
                            Delete
                        </Menu.Item>
                    ) : null}

                    {row?.status?.toLowerCase() === 'submitted' ? (
                        <Menu.Item
                            color="red"
                            icon={<IonIcon className="action-ion-icon" name="swap-horizontal-outline" />}
                            onClick={() => {
                                rejectLoanMerge(row, dispatch);
                            }}
                        >
                            Reject
                        </Menu.Item>
                    ) : null}
                </Menu.Dropdown>
            </Menu>

            {showDeleteModal && (
                <DeleteConfirmation
                    onConfirm={handleDelete}
                    onCancel={() => setShowDeleteModal(false)}
                    message="Delete This Draft Loan Merge?"
                />
            )}
        </div>
    );
}